import { Grid2, Stack } from '@mui/material'

import {
  ArtistStoreCollectionBoxFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  ArtistStoreCollectionBox,
  ArtistStoreCollectionBoxSkeleton,
  PointDisplayPurchase,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  isLoggedIn: boolean
  point: string
  collections: Maybe<{ edges: { node: ArtistStoreCollectionBoxFragment }[] }>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const StoreCollectionsSelectTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('storeCollectionsSelect')
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ artistId: string }>()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.StoreCollectionsSelect} />
      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title', 'コレクションから探す'),
          defaultBackPage: generatePath(routerPages.ArtistStore, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
          right: state.isLoggedIn && !!config.modules.prepaidPoint && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mr: -2 }}
            >
              <PointDisplayPurchase point={state.point} />
            </Stack>
          ),
        }}
      >
        <ScrollFetcher
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          component={Grid2}
          componentProps={{ spacing: 3, container: true }}
          itemComponent={Grid2}
          itemComponentProps={{ size: { xs: 6, md: 6 } }}
          nodes={state.collections?.edges.map(({ node }) => (
            <ArtistStoreCollectionBox
              conductorName="storeCollectionList"
              collection={node}
              key={node.id}
            />
          ))}
          loadingComponent={
            <Grid2 container spacing={3}>
              {Array.from(Array(6).keys()).map(key => (
                <Grid2 size={{ xs: 6, md: 6 }} key={key}>
                  <ArtistStoreCollectionBoxSkeleton />
                </Grid2>
              ))}
            </Grid2>
          }
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
